import React, {useCallback, useEffect, useMemo, useState} from "react";
import Modal from "../Modal/Modal";
import DropdownInput from "../DropdownInput";
import TimePicker from "../TimePicker";
import DatePicker from "../DatePicker";
import {Button} from "@material-tailwind/react";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {ErrorHandler} from "../../hooks/errorHandler";
import {APILink} from "../../types/APILink";
import {useSystemContext} from "../../types/contexts/SystemContext";
import {
    ReportingSchedule,
    ReportingSubscriptionRecord,
    ReportingSubscriptionRecipientPayload,
    ReportingType
} from "../../types/Responses/ReportingSubscriptionsResponse";
import {FooterListItem} from "../Sidebar/FooterMenu";
import {UserShortInfo} from "../../types/Responses/PayoutLimitsResponse";
import FilterTagGroups, {FilterTagGroupDisplay} from "../FilterTagGroups";
import {
    buildReportingFilterGroups,
    FilterGroupKey,
    normalizeReportingFilters,
    ReportingFilterLookups,
    SimpleEntity
} from "../../utils/reportingFilters";
import {useUserContext} from "../../types/contexts/UserContext";

type RecipientOption = {
    type: 'user' | 'email';
    id?: number;
    name?: string | null;
    email: string;
    avatar?: string | null;
};

type RecipientAutocompleteOption = RecipientOption & { inputValue?: string };

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const buildDefaultFormState = () => ({
    reporting_type_id: '',
    schedule_id: '',
    time: '',
    start_date: '',
    format: 'csv'
});

interface ReportingSubscriptionModalProps {
    open: boolean;
    modalRef: React.ForwardedRef<any>;
    onClose: () => void;
    onSuccess?: () => void;
    subscription?: ReportingSubscriptionRecord | null;
    defaultFilters?: ReportingSubscriptionRecord['filters'];
    defaultReportingTypeName?: string;
    metadata?: ReportingFilterLookups;
}

const ReportingSubscriptionModal: React.FC<ReportingSubscriptionModalProps> = ({
    open,
    modalRef,
    onClose,
    onSuccess,
    subscription,
    defaultFilters,
    defaultReportingTypeName,
    metadata
}) => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const system = useSystemContext();

    const [types, setTypes] = useState<ReportingType[]>([]);
    const [schedules, setSchedules] = useState<ReportingSchedule[]>([]);
    const [usersList, setUsersList] = useState<UserShortInfo[]>([]);
    const [recipients, setRecipients] = useState<RecipientAutocompleteOption[]>([]);
    const [formState, setFormState] = useState(buildDefaultFormState());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasLoadedData, setHasLoadedData] = useState(false);
    const [prefillTypeApplied, setPrefillTypeApplied] = useState(false);
    const [lockedFilters, setLockedFilters] = useState<ReportingSubscriptionRecord['filters']>(null);
    const userContext = useUserContext();
    const currentUser = userContext?.user ?? null;

    const permissions = useMemo(() => currentUser?.permissions ?? [], [currentUser]);
    const canManageRecipients = useMemo(() => permissions.includes('allow add extra recipient'), [permissions]);
    const canAddExternalRecipients = useMemo(() => canManageRecipients && permissions.includes('allow_add_external_recipient'), [permissions, canManageRecipients]);

    const allowedDomains = useMemo(() => {
        if (!system?.system?.allowed_email_domains) {
            return [];
        }
        return system.system.allowed_email_domains.map((domain) => domain.toLowerCase());
    }, [system]);

    const isEmailDomainAllowed = useCallback((email: string) => {
        if (allowedDomains.length === 0) {
            return true;
        }
        const domain = email.includes('@') ? email.split('@').pop()?.toLowerCase() ?? '' : '';
        return domain.length > 0 && allowedDomains.includes(domain);
    }, [allowedDomains]);

    const loadStaticData = useCallback(() => {
        Promise.all([
            API.getReportingTypes(),
            API.getReportingSchedules(),
            API.getUsersList(1)
        ]).then(([typesResponse, schedulesResponse, usersResponse]: [AxiosResponse, AxiosResponse, AxiosResponse]) => {
            const typePayload = typesResponse.data as { data?: ReportingType[] };
            const schedulesPayload = schedulesResponse.data as { data?: ReportingSchedule[] };
            const usersPayload = usersResponse.data as { data?: UserShortInfo[] };
            setTypes(typePayload.data ?? []);
            const sortedSchedules = (schedulesPayload.data ?? []).slice().sort((a, b) => {
                const aIsManual = a.period === 0;
                const bIsManual = b.period === 0;
                if (aIsManual && bIsManual) {
                    return 0;
                }
                if (aIsManual) {
                    return 1;
                }
                if (bIsManual) {
                    return -1;
                }
                return a.period - b.period;
            });
            setSchedules(sortedSchedules);
            setUsersList(usersPayload.data ?? []);
            setHasLoadedData(true);
        }).catch((e) => {
            ErrorHandler(e, `get${APILink.GET_REPORTING_TYPES}`, toast, handleError);
        });
    }, [toast, handleError]);

    useEffect(() => {
        if (open && !hasLoadedData) {
            loadStaticData();
        }
    }, [open, hasLoadedData, loadStaticData]);

    useEffect(() => {
        if (!open) {
            setRecipients([]);
            setFormState(buildDefaultFormState());
            setLockedFilters(null);
            setPrefillTypeApplied(false);
            return;
        }
        if (subscription) {
            setFormState({
                reporting_type_id: subscription.reporting_type_id.toString(),
                schedule_id: subscription.schedule?.id?.toString() ?? '',
                time: subscription.time,
                start_date: subscription.start_date ?? '',
                format: subscription.format
            });
            const userRecipients = subscription.recipients.users.map<RecipientAutocompleteOption>((recipient) => ({
                type: 'user',
                id: recipient.id,
                name: recipient.name,
                email: recipient.email ?? '',
                avatar: recipient.avatar ?? null
            }));
            const emailRecipients = subscription.recipients.emails.map<RecipientAutocompleteOption>((email) => ({
                type: 'email',
                email,
                name: email
            }));
            if (canManageRecipients) {
                setRecipients([...userRecipients, ...emailRecipients]);
            } else {
                setRecipients([]);
            }
            setLockedFilters(normalizeReportingFilters(subscription.filters ?? null));
            setPrefillTypeApplied(true);
        } else {
            setFormState(buildDefaultFormState());
            setRecipients([]);
            setLockedFilters(normalizeReportingFilters(defaultFilters ?? null));
            setPrefillTypeApplied(false);
        }
    }, [open, subscription, defaultFilters, canManageRecipients]);

    useEffect(() => {
        if (!open || subscription || !defaultReportingTypeName || prefillTypeApplied || types.length === 0) {
            return;
        }
        const type = types.find((item) => item.name === defaultReportingTypeName);
        if (type) {
            setFormState((prev) => ({
                ...prev,
                reporting_type_id: type.id.toString()
            }));
            setPrefillTypeApplied(true);
        }
    }, [open, subscription, defaultReportingTypeName, types, prefillTypeApplied]);

    const selectedSchedule = useMemo(() => {
        const parsedId = parseInt(formState.schedule_id, 10);
        if (Number.isNaN(parsedId)) {
            return undefined;
        }
        return schedules.find((schedule) => schedule.id === parsedId);
    }, [formState.schedule_id, schedules]);

    const requiresStartDate = useMemo(() => {
        if (!selectedSchedule) {
            return false;
        }
        return selectedSchedule.code === 'weekly' || selectedSchedule.code === 'monthly';
    }, [selectedSchedule]);

    useEffect(() => {
        if (requiresStartDate && !formState.start_date) {
            setFormState((prev) => ({
                ...prev,
                start_date: moment().format('YYYY-MM-DD')
            }));
        }
    }, [requiresStartDate, formState.start_date]);

    const formatOptions = useMemo(() => ([
        {name: 'CSV', code: 'csv'},
        {name: 'XLSX', code: 'xlsx'}
    ]), []);

    const typeOptions = useMemo(() => types.map((type) => ({
        id: type.id,
        name: type.name
    })), [types]);

    const scheduleOptions = useMemo(() => schedules.map((schedule) => ({
        id: schedule.id,
        name: schedule.name
    })), [schedules]);

    const userRecipientOptions = useMemo<RecipientAutocompleteOption[]>(() => usersList.map((u) => ({
        type: 'user',
        id: parseInt(u.id, 10),
        name: u.name,
        email: u.email ?? '',
        avatar: u.image
    })), [usersList]);

    const getRecipientValue = useCallback((recipient: RecipientOption) => {
        if (recipient.type === 'user' && recipient.id !== undefined) {
            return `user-${recipient.id}`;
        }
        return `email-${recipient.email.toLowerCase()}`;
    }, []);

    const recipientDropdownOptions = useMemo<FooterListItem[]>(() => userRecipientOptions.map((option) => ({
        id: option.id,
        name: option.name ?? option.email,
        code: option.email,
        value: getRecipientValue(option),
        data: option
    })), [userRecipientOptions, getRecipientValue]);

    const recipientSelectedValues = useMemo<(string | number)[]>(() => recipients.map((recipient) => getRecipientValue(recipient)), [recipients, getRecipientValue]);

    const selectedRecipientItems = useMemo<FooterListItem[]>(() => recipients.map((recipient) => ({
        name: recipient.type === 'user' ? (recipient.name ?? recipient.email) : recipient.email,
        code: recipient.email,
        value: getRecipientValue(recipient),
        data: recipient
    })), [recipients, getRecipientValue]);

    const renderRecipientOption = useCallback((item: FooterListItem) => {
        const data = item.data as RecipientAutocompleteOption | undefined;
        if (data?.type === 'user') {
            return (
                <div className="flex w-full items-center gap-3 py-1">
                    <div className="flex flex-col leading-tight">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{data.name}</span>
                        <span className="text-xs text-gray-500">{data.email}</span>
                    </div>
                </div>
            );
        }
        return <span>{item.name}</span>;
    }, []);

    const handleAddEmailRecipient = useCallback((value: string) => {
        if (!canAddExternalRecipients) {
            return;
        }
        const trimmed = value.trim();
        if (!emailRegex.test(trimmed)) {
            toast?.show(t('admin.subscriptions.recipients.invalid_email'), 'bg-red-500');
            return;
        }
        if (!isEmailDomainAllowed(trimmed)) {
            toast?.show(t('admin.subscriptions.recipients.email_domain_not_allowed'), 'bg-red-500');
            return;
        }
        const normalized = trimmed.toLowerCase();
        setRecipients((prev) => {
            if (prev.some((recipient) => recipient.email.toLowerCase() === normalized)) {
                return prev;
            }
            return [...prev, {
                type: 'email',
                email: normalized,
                name: trimmed
            }];
        });
    }, [toast, t, isEmailDomainAllowed, canAddExternalRecipients]);

    const onRecipientDropdownChange = useCallback((value: string | number) => {
        const parsed = value.toString();
        if (parsed.startsWith('user-')) {
            const idPart = parsed.replace('user-', '');
            const parsedId = parseInt(idPart, 10);
            if (Number.isNaN(parsedId)) {
                return;
            }
            const option = userRecipientOptions.find((user) => user.id === parsedId);
            if (!option) {
                return;
            }
            setRecipients((prev) => {
                if (prev.some((recipient) => recipient.type === 'user' && recipient.id === parsedId)) {
                    return prev;
                }
                return [...prev, option];
            });
            return;
        }
        if (parsed.startsWith('email-')) {
            if (canAddExternalRecipients) {
                handleAddEmailRecipient(parsed.replace('email-', ''));
            }
        }
    }, [userRecipientOptions, handleAddEmailRecipient, canAddExternalRecipients]);

    const onRecipientRemove = useCallback((index: number) => {
        setRecipients((prev) => prev.filter((_, idx) => idx !== index));
    }, []);

    const canCreateRecipient = useCallback((query: string) => {
        if (!canAddExternalRecipients) {
            return false;
        }
        const trimmed = query.trim();
        if (!emailRegex.test(trimmed)) {
            return false;
        }
        if (!isEmailDomainAllowed(trimmed)) {
            return false;
        }
        const normalized = trimmed.toLowerCase();
        return !recipients.some((recipient) => recipient.email.toLowerCase() === normalized);
    }, [recipients, isEmailDomainAllowed, canAddExternalRecipients]);

    const buildRecipientsPayload = useCallback((): ReportingSubscriptionRecipientPayload => {
        const users = recipients
            .filter((recipient) => recipient.type === 'user' && recipient.id)
            .map((recipient) => recipient.id as number)
            .filter((value, index, array) => array.indexOf(value) === index);
        const emails = recipients
            .filter((recipient) => recipient.type === 'email')
            .map((recipient) => recipient.email.toLowerCase())
            .filter((value, index, array) => array.indexOf(value) === index);
        return {users, emails};
    }, [recipients]);

    const onScheduleChange = useCallback((value: string | number) => {
        const scheduleId = value.toString();
        const parsedId = parseInt(scheduleId, 10);
        const schedule = schedules.find((item) => item.id === parsedId);
        setFormState((prev) => ({
            ...prev,
            schedule_id: scheduleId,
            start_date: schedule && (schedule.code === 'weekly' || schedule.code === 'monthly')
                ? (prev.start_date && prev.start_date.length ? prev.start_date : moment().format('YYYY-MM-DD'))
                : ''
        }));
    }, [schedules]);

    const submitForm = useCallback(() => {
        if (!formState.reporting_type_id || !formState.schedule_id || !formState.time) {
            toast?.show(t('admin.subscriptions.form.missing_fields'), 'bg-red-500');
            return;
        }
        if (requiresStartDate && !formState.start_date) {
            toast?.show(t('admin.subscriptions.form.start_date_required'), 'bg-red-500');
            return;
        }
        const payload: ReportingSubscriptionRecipientPayload | null = canManageRecipients ? buildRecipientsPayload() : null;
        setIsSubmitting(true);
        const requestPayload: Record<string, unknown> = {
            reporting_type_id: parseInt(formState.reporting_type_id, 10),
            schedule_id: parseInt(formState.schedule_id, 10),
            time: formState.time,
            start_date: requiresStartDate ? formState.start_date : null,
            format: formState.format
        };
        if (canManageRecipients && payload) {
            requestPayload['recipients'] = payload;
        }
        if (!subscription && lockedFilters && Object.keys(lockedFilters).length > 0) {
            requestPayload['filters'] = lockedFilters;
        }
        const request = subscription ?
            API.updateReportingSubscription(subscription.id, requestPayload) :
            API.createReportingSubscription(requestPayload);
        request.then(() => {
            toast?.show(subscription ? t('admin.subscriptions.updated') : t('admin.subscriptions.created'), 'bg-teal-500');
            setIsSubmitting(false);
            onClose();
            onSuccess?.();
        }).catch(e => {
            setIsSubmitting(false);
            ErrorHandler(e, subscription ? `put${APILink.UPDATE_REPORTING_SUBSCRIPTION.replace('$subscription', subscription.id.toString())}` : `post${APILink.GET_REPORTING_SUBSCRIPTIONS}`, toast, handleError);
        });
    }, [formState, requiresStartDate, subscription, lockedFilters, toast, t, onClose, onSuccess, handleError, buildRecipientsPayload, canManageRecipients]);

    const currentFilters = lockedFilters;

    const lookupUsers = useMemo<SimpleEntity[] | undefined>(() => {
        if (metadata?.users && metadata.users.length > 0) {
            return metadata.users;
        }
        if (!usersList.length) {
            return undefined;
        }
        return usersList.map((user) => ({
            id: parseInt(user.id, 10),
            name: user.name ?? user.email ?? `#${user.id}`
        }));
    }, [metadata?.users, usersList]);

    const filterLookups = useMemo<ReportingFilterLookups>(() => ({
        brokers: metadata?.brokers,
        paymentMethods: metadata?.paymentMethods,
        countries: metadata?.countries,
        users: lookupUsers,
        statuses: system?.system?.transactionStatuses?.map((status) => ({id: status.id, name: status.name})),
        providers: system?.system?.providers?.map((provider) => ({id: provider.id, name: provider.name})),
        currencies: system?.system?.currencies?.map((currency) => ({id: currency.id, code: currency.code}))
    }), [metadata, lookupUsers, system]);

    const filterLabels: Record<FilterGroupKey, string> = useMemo(() => ({
        brokers: t('admin.subscriptions.filters.brokers'),
        users: t('admin.subscriptions.filters.users'),
        payment_methods: t('admin.subscriptions.filters.payment_methods'),
        providers: t('admin.subscriptions.filters.providers'),
        statuses: t('admin.subscriptions.filters.statuses'),
        countries: t('admin.subscriptions.filters.countries'),
        currencies: t('admin.subscriptions.filters.currencies')
    }), [t]);

    const groupedFilters = useMemo<FilterTagGroupDisplay[]>(() => {
        const groups = buildReportingFilterGroups(currentFilters, filterLookups);
        return groups.map((group) => ({
            title: filterLabels[group.key],
            tags: group.tags
        }));
    }, [currentFilters, filterLookups, filterLabels]);

    const hasLockedFilters = Boolean(currentFilters && Object.keys(currentFilters).length > 0);

    if (!open) {
        return null;
    }

    return (
        <Modal refer={modalRef}
               showModal={open}
               title={subscription ? t('admin.subscriptions.form.edit_title') : t('admin.subscriptions.button.new')}>
            <div className="w-full max-w-md flex flex-col gap-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-0">
                    <div className={hasLockedFilters ? 'opacity-70 pointer-events-none' : ''}>
                        <DropdownInput name={t('admin.subscriptions.form.type')}
                                       list={typeOptions}
                                       selected={formState.reporting_type_id}
                                       onChange={(value) => {
                                           if (hasLockedFilters) {
                                               return;
                                           }
                                           setFormState({
                                               ...formState,
                                               reporting_type_id: value.toString()
                                           });
                                       }}
                                       searchable={true}/>
                    </div>
                    <DropdownInput name={t('admin.subscriptions.form.schedule')}
                                   list={scheduleOptions}
                                   selected={formState.schedule_id}
                                   onChange={onScheduleChange}
                                   searchable={true}/>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-0">
                    <DropdownInput name={t('admin.subscriptions.form.format')}
                                   list={formatOptions}
                                   selected={formState.format}
                                   onChange={(value) => setFormState({...formState, format: value.toString()})}/>
                    <TimePicker
                        label={t('admin.subscriptions.form.time')}
                        placeholder={t('admin.subscriptions.form.time')}
                        value={formState.time}
                        onChange={(nextTime) => setFormState({...formState, time: nextTime})}
                    />
                </div>
                {requiresStartDate && (
                    <DatePicker
                        label={t('admin.subscriptions.form.start_date')}
                        date={formState.start_date && formState.start_date.length ? formState.start_date : moment().format('YYYY-MM-DD')}
                        onChange={(nextDate) => setFormState({...formState, start_date: nextDate})}
                    />
                )}

                <div className="mt-2 border rounded-lg px-3 py-2">
                    <div className="text-sm font-semibold mb-2">{t('admin.subscriptions.filters.title')}</div>
                    <FilterTagGroups groups={groupedFilters} emptyLabel={t('admin.subscriptions.filters.empty')}/>
                </div>

                {canManageRecipients && (
                    <DropdownInput
                        name={t('admin.subscriptions.form.recipients')}
                        list={recipientDropdownOptions}
                        selected={recipientSelectedValues}
                        selectedItems={selectedRecipientItems}
                        onChange={onRecipientDropdownChange}
                        onRemove={onRecipientRemove}
                        searchable
                        multiple
                        renderOption={renderRecipientOption}
                        noResultsActionLabel={canAddExternalRecipients ? (query => t('admin.subscriptions.recipients.send_to', {value: query})) : undefined}
                        noResultsActionValidator={canAddExternalRecipients ? canCreateRecipient : undefined}
                        onNoResultsAction={canAddExternalRecipients ? handleAddEmailRecipient : undefined}
                        menuClassName="w-full"
                        buttonClassName="min-h-[auto!important]"
                    />
                )}

                <div className="mt-4 text-center">
                    <Button className="mt-4"
                            color="teal"
                            disabled={isSubmitting}
                            onClick={submitForm}>{t(subscription ? 'admin.subscriptions.form.submit.update' : 'admin.subscriptions.form.submit.create')}</Button>
                </div>
                <div className="text-center -mt-1">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={onClose}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    );
};

export default ReportingSubscriptionModal;
